import React, { useState } from 'react';
import { apiClient, API_ENDPOINTS } from '../config/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserStatus } from '../types';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import TermsAgreementSection from './Terms/TermsAgreementSection';
import type { TermsAgreementState } from '../types/terms';
import { useActiveTerms, useSubmitTermsAgreement } from '../hooks/useTerms';

const NormalRegistrationPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [termsState, setTermsState] = useState<TermsAgreementState>({
    serviceTerms: false,
    privacyConsent: false,
    marketingConsent: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: boolean}>({});
  
  const navigate = useNavigate();
  const { login } = useAuth();
  const { terms: activeTerms } = useActiveTerms();
  const { submit: submitTermsAgreement } = useSubmitTermsAgreement();

  // 페이지 진입 시 기존 인증 정보 정리
  React.useEffect(() => {
    apiClient.clearAuthData();
  }, []);

  const validateForm = () => {
    const errors: {[key: string]: boolean} = {};
    let hasError = false;

    if (!formData.name.trim()) {
      errors.name = true;
      hasError = true;
    }
    
    if (!formData.email.trim()) {
      errors.email = true;
      hasError = true;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = true;
      hasError = true;
      setError('올바른 이메일 주소를 입력해주세요.');
      setFieldErrors(errors);
      return false;
    }
    
    if (!formData.password) {
      errors.password = true;
      hasError = true;
    } else if (formData.password.length < 8) {
      errors.password = true;
      hasError = true;
      setError('비밀번호는 최소 8자 이상이어야 합니다.');
      setFieldErrors(errors);
      return false;
    }
    
    if (!formData.confirmPassword) {
      errors.confirmPassword = true;
      hasError = true;
    } else if (formData.password !== formData.confirmPassword) {
      errors.password = true;
      errors.confirmPassword = true;
      hasError = true;
      setError('비밀번호가 일치하지 않습니다.');
      setFieldErrors(errors);
      return false;
    }

    if (hasError) {
      setError('모든 필드를 올바르게 입력해주세요.');
      setFieldErrors(errors);
      return false;
    }

    if (!termsState.serviceTerms || !termsState.privacyConsent) {
      setError('필수 약관(이용약관, 개인정보 동의)에 동의해주세요.');
      return false;
    }

    setFieldErrors({});
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      // join API 호출
      const joinData = {
        email: formData.email,
        password: formData.password,
        userName: formData.name,
      };

      const joinResponse: any = await apiClient.post(API_ENDPOINTS.AUTH.JOIN, joinData);
      console.log('join API 응답:', joinResponse);

      // 액세스 토큰만 localStorage에 저장 (refreshToken은 HttpOnly 쿠키)
      if (joinResponse.result?.tokenDto?.accessToken) {
        localStorage.setItem('accessToken', joinResponse.result.tokenDto.accessToken);
      }

      // 약관 동의 제출 (termsId 기반)
      const findId = (type: string) => activeTerms.find((t) => t.type === type)?.id;
      const agreements = [
        { termsId: findId('SERVICE_TERMS')!, isAgreed: true },
        { termsId: findId('PRIVACY_CONSENT')!, isAgreed: true },
        { termsId: findId('MARKETING_CONSENT')!, isAgreed: termsState.marketingConsent },
      ].filter((a) => a.termsId != null);
      if (agreements.length > 0) {
        await submitTermsAgreement({ agreements }).catch(() => {});
      }

      // 사용자 정보 확인
      const user = joinResponse.result?.userInfo;
      if (user) {
        // 인증 성공 시 로그인 처리 (카카오와 동일한 방식)
        login(user);

        // 사용자 상태에 따른 리다이렉트 처리
        if (user.status === UserStatus.GUEST) {
          // GUEST 상태 - 프로필 추가 정보 입력 페이지로 이동
          const normalUserData = {
            type: 'normal',
            userId: user.userId,
            email: user.email,
            name: user.userName,
            profileImage: '',
          };

          // 세션 스토리지에 사용자 정보 임시 저장
          sessionStorage.setItem('registrationUserData', JSON.stringify(normalUserData));
          setTimeout(() => navigate('/register/profile'), 100);
        } else {
          // PENDING, APPROVED, REJECTED 상태는 대시보드로 이동
          setTimeout(() => navigate('/dashboard'), 100);
        }
      } else {
        throw new Error('사용자 정보를 받지 못했습니다.');
      }
    } catch (err: any) {
      setError(err.message || '회원가입 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // 입력 시 해당 필드의 에러 제거
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: false }));
    }
    // 에러 메시지 제거
    if (error) setError('');
  };

  return (
    <Box>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/register')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div">
            이메일 회원가입
          </Typography>
        </Toolbar>
      </AppBar>

      <Container component="main" maxWidth="sm" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom color="primary">
              Silver Connect
            </Typography>
            <Typography variant="h6" color="text.secondary">
              회원 정보를 입력해주세요
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label="이름"
              name="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={fieldErrors.name}
              placeholder="실명을 입력해주세요"
            />

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="이메일 주소"
              name="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              error={fieldErrors.email}
              placeholder="example@email.com"
            />

            <TextField
              margin="normal"
              required
              fullWidth
              id="password"
              label="비밀번호"
              name="password"
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              error={fieldErrors.password}
              helperText="최소 8자 이상"
            />

            <TextField
              margin="normal"
              required
              fullWidth
              id="confirmPassword"
              label="비밀번호 확인"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              error={fieldErrors.confirmPassword}
              helperText="비밀번호를 다시 입력해주세요"
            />

            {/* 약관 동의 */}
            <TermsAgreementSection
              value={termsState}
              onChange={setTermsState}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !termsState.serviceTerms || !termsState.privacyConsent}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              {loading ? '처리 중...' : '다음'}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2">
              이미 계정이 있으신가요?{' '}
              <Button
                variant="text"
                onClick={() => navigate('/login')}
                sx={{ color: 'primary.main', p: 0, minWidth: 'auto' }}
              >
                로그인하기
              </Button>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default NormalRegistrationPage;